import Dialog from "../../dialog/dialog.index";
import { useAppSelector } from "../../../../redux/hooks";
import {
    handleSetEnoughSpaceFiles,
    handleSetEnoughSpaceFileStatus,
    handleSetEnoughSpaceModal,
    handleSetFileUploadState,
    handleSetFolderUploadState
} from "../../../../scripts/upload";
import { DialogSize } from "../../dialog/dialog.interface";
import ProgressBar from "@ramonak/react-progress-bar";
import { useEffect, useState } from "react";
import { allFormatRegex, getFileNameWithoutFormatWithName, getPathHash, UnitConvertStandard } from "../../../../scripts/helpers";
import { setResourceIcon } from "../../../../scripts/resourceHelper";
import CheckBox from "../../checkbox/checkbox.index";
import Button from "../../button/button.index";
import { ButtonType } from "../../button/button.interface";
import PATH from "../../../../urls/path";
import { Link, useLocation } from "react-router-dom";
import { ENVIRONMENT } from "../../../../environment";
import { shallowEqual } from "react-redux";

export default function EnoughSpace(): JSX.Element {
    const [files, setFiles] = useState<
        {
            id: string;
            file: File;
            status: boolean;
            folder: boolean;
        }[]
    >([]);

    const { quota } = useAppSelector(({ fileManager: { quota } }) => ({ quota }), shallowEqual);
    const { enoughSpaceFiles, enoughSpaceModal } = useAppSelector(
        ({ upload: { enoughSpaceFiles, enoughSpaceModal } }) => ({ enoughSpaceFiles, enoughSpaceModal }),
        shallowEqual
    );
    const location = useLocation();

    const getUsedSpace = () => {
        if (quota) {
            return (
                quota!.used +
                [...enoughSpaceFiles.singleFiles.filter((item) => item.status), ...enoughSpaceFiles.folderFiles.filter((item) => item.status)].reduce(
                    (total, currentValue) => total + currentValue.file.size,
                    0
                )
            );
        } else {
            return 0;
        }
    };

    useEffect(() => {
        if (enoughSpaceModal) {
            setFiles(
                [...enoughSpaceFiles.singleFiles, ...enoughSpaceFiles.folderFiles]
                    .sort((a, b) => (a.file.size > b.file.size ? -1 : 1))
                    .sort((a, b) => (a.status ? -1 : 1))
            );
        }
    }, [enoughSpaceModal]);

    const handleUpdateFile = (id: string) => {
        setFiles((prev) => {
            return prev.map((item) => {
                if (item.id === id) {
                    return {
                        ...item,
                        status: !item.status
                    };
                } else {
                    return item;
                }
            });
        });
    };

    return (
        <Dialog
            status={enoughSpaceModal}
            onClose={() => {
                handleSetEnoughSpaceModal(false);
                setTimeout(() => {
                    handleSetEnoughSpaceFiles({ singleFiles: [], folderFiles: [] });
                }, 500);
            }}
            title="آزاد‌سازی فضای ذخیره‌سازی"
            size={DialogSize.MD}
            childrenParentClassName="!overflow-hidden"
        >
            <div className="flex flex-col h-full">
                <div className="px-4">
                    <p className="text-secondary-600 text-[13px] leading-[32px]">
                        به علت کمبود فضای ذخیره‌سازی برای ادامه فرایند آپلود نیاز است لازم است فایل‌های خود را برای ادامه فرایند انتخاب کنید یا فایل
                        هایی که کمتر استفاده شده از{" "}
                        <Link to={PATH.files} target="_blank" className="text-primary-600 cursor-pointer">
                            {ENVIRONMENT.brandName} فایل‌های من
                        </Link>{" "}
                        خود حذف کنید، همچنین می‌توانید با استفاده از{" "}
                        <Link to={PATH.planRecommendation} target="_blank" className="text-primary-600 cursor-pointer">
                            {" "}
                            فضای ابری‌های ویژه {ENVIRONMENT.brandName}
                        </Link>{" "}
                        حجم فضای ذخیره‌سازی خود را افزایش دهید.
                    </p>
                    {quota && (
                        <div className="my-4">
                            <ProgressBar
                                completed={(100 * getUsedSpace()) / (quota.total_plan + quota.total_gift)}
                                labelSize={"0px"}
                                dir="rtl"
                                height={"8px"}
                                bgColor={`${getUsedSpace() > quota.total_plan + quota.total_gift ? "#FFA6B6" : "linear-gradient(95.08deg, #0F5EF9 2.49%, #2FECE6 97.19%)"}`}
                            />
                            <div className="flex items-center justify-between gap-4 mt-2">
                                <div className="text-secondary-400 text-[12px]">
                                    {getUsedSpace() > quota.total_plan + quota.total_gift
                                        ? "حافظهٔ کافی موجود نیست."
                                        : `${UnitConvertStandard(getUsedSpace()).value.toFixed(1)} از ${UnitConvertStandard(quota.total_plan + quota.total_gift).value.toFixed(0)} ${UnitConvertStandard(quota.total_plan + quota.total_gift, "fa").unit} استفاده شده است.`}
                                </div>
                                <div className="text-secondary-400 text-[12px]">
                                    {getUsedSpace() > quota.total_plan + quota.total_gift
                                        ? `(${UnitConvertStandard(getUsedSpace() - (quota.total_plan + quota.total_gift)).value.toFixed(1)} ${UnitConvertStandard(getUsedSpace() - (quota.total_plan + quota.total_gift), "fa").unit} حجم اضافه انتخاب شده.)`
                                        : `(${UnitConvertStandard(quota.total_plan + quota.total_gift - getUsedSpace()).value.toFixed(0)} ${UnitConvertStandard(quota.total_plan + quota.total_gift - getUsedSpace(), "fa").unit} فضای باقی‌مانده)`}
                                </div>
                            </div>
                        </div>
                    )}
                    <hr />
                </div>
                <div className="flex-1 overflow-auto no-scrollbar my-2">
                    {files.map((item) => {
                        const format = item.file.name ? item.file.name.match(allFormatRegex) : "";
                        return (
                            <div
                                key={item.id}
                                className="flex items-center gap-4 cursor-pointer hover:bg-primary-50 transition-all p-4"
                                onClick={() => {
                                    handleSetEnoughSpaceFileStatus(item.id, item.folder);
                                    handleUpdateFile(item.id);
                                }}
                            >
                                <div className="relative drop-shadow-md">
                                    <div
                                        className={`text-white drop-shadow-md text-[10px] ${format && (format[0].slice(1).length === 6 || format[0].slice(1).length === 5) && "text-[7px]"} ${format && format[0].slice(1).length === 4 && "text-[10px]"} absolute top-1 right-[3px] rounded-sm w-full tracking-widest uppercase`}
                                    >
                                        {format ? format[0].slice(1) : null}
                                    </div>
                                    <img
                                        className="w-[40px]"
                                        loading="lazy"
                                        src={setResourceIcon(getFileNameWithoutFormatWithName(item.file.name).format)}
                                        alt={item.file.name}
                                    />
                                </div>
                                <div className="flex-1">
                                    <p className="text-secondary-800 text-[14px] font-[500] mb-1">{item.file.name}</p>
                                    <p className="text-secondary-600 text-[13px]">{`${UnitConvertStandard(item.file.size).value.toFixed(0)} ${UnitConvertStandard(item.file.size, "fa").unit}`}</p>
                                </div>
                                <CheckBox label={""} onChange={() => true} defaultValue={item.status} />
                            </div>
                        );
                    })}
                </div>
                {quota && (
                    <div className="px-4 mb-4 mt-2 flex items-center justify-end gap-4">
                        <Button
                            autoFocus={false}
                            onClick={() => {
                                handleSetEnoughSpaceModal(false);
                                setTimeout(() => {
                                    handleSetEnoughSpaceFiles({ singleFiles: [], folderFiles: [] });
                                }, 500);
                            }}
                            text="انصراف"
                            type={ButtonType.TERTIARY}
                        />
                        <Button
                            autoFocus={false}
                            onClick={() => {
                                const files = enoughSpaceFiles;
                                handleSetFolderUploadState({
                                    files: files.folderFiles.filter((item) => item.status).map((item) => item.file),
                                    hash: location.pathname === PATH.shared ? getPathHash() : undefined
                                });
                                handleSetFileUploadState({
                                    files: files.singleFiles.filter((item) => item.status).map((item) => item.file),
                                    hash: location.pathname === PATH.shared ? getPathHash() : undefined
                                });
                                handleSetEnoughSpaceModal(false);
                                setTimeout(() => {
                                    handleSetEnoughSpaceFiles({ singleFiles: [], folderFiles: [] });
                                }, 500);
                            }}
                            text="ادامهٔ آپلود"
                            className="w-[200px]"
                            disable={
                                [
                                    ...enoughSpaceFiles.singleFiles.filter((item) => item.status),
                                    ...enoughSpaceFiles.folderFiles.filter((item) => item.status)
                                ].filter((item) => item.status).length === 0 || getUsedSpace() > quota.total_plan + quota.total_gift
                            }
                        />
                    </div>
                )}
            </div>
        </Dialog>
    );
}
